import { css } from 'styled-components';
import { PxToRem, SIZES, PX_TO_REM, OFFGRID_SIZES } from './sizes';
import { buttonBorderRadius } from '../../components/Button/buttonThemeConfig';
import { EORA_NUMBERS } from '../numbers/eoraNumbers';
import { Theme } from '@cmctechnology/webinvest-constants';
import { eoraThemeName } from '../../themes/themeNames';

/**
 * @deprecated Use StandardBorderRadiusSize or ButtonTabBadgeRadiusSize
 */
export type BorderRadiusSize =
  | typeof SIZES.MICRO
  /**
   * @deprecated Balh
   */
  | typeof SIZES.XXX_SMALL
  | typeof SIZES.XX_SMALL
  | typeof SIZES.X_SMALL
  | typeof SIZES.SMALL;

export type OffgridBorderRadiusSize =
  | typeof OFFGRID_SIZES.OFFGRID_2
  | typeof OFFGRID_SIZES.OFFGRID_7
  | typeof OFFGRID_SIZES.OFFGRID_9
  | typeof OFFGRID_SIZES.OFFGRID_10
  | typeof OFFGRID_SIZES.OFFGRID_12
  | typeof OFFGRID_SIZES.OFFGRID_14
  | typeof OFFGRID_SIZES.OFFGRID_18
  | typeof OFFGRID_SIZES.OFFGRID_20
  | typeof OFFGRID_SIZES.OFFGRID_22
  | typeof OFFGRID_SIZES.OFFGRID_26;

export type SpeicalBorderRadiusSize = 'input' | 'panel';

export type StandardBorderRadiusSize = OffgridBorderRadiusSize | typeof SIZES.SMALL | typeof SIZES.MEDIUM | typeof SIZES.LARGE | typeof SIZES.X_LARGE;

export type ButtonTabBadgeRadiusSize =
  | typeof SIZES.XX_SMALL
  | typeof OFFGRID_SIZES.OFFGRID_9
  | typeof OFFGRID_SIZES.OFFGRID_10
  | typeof SIZES.X_SMALL
  | typeof SIZES.SMALL
  | typeof SIZES.MEDIUM
  | typeof SIZES.LARGE
  | typeof SIZES.NONE;

/**
 * @deprecated Use standardBorderRadiusSizes or buttonTabBadgeRadiusSizes
 */
export const borderRadiusSizes: Record<BorderRadiusSize, PxToRem> = {
  [SIZES.MICRO]: PX_TO_REM[4],
  [SIZES.XXX_SMALL]: PX_TO_REM[8],
  [SIZES.XX_SMALL]: PX_TO_REM[10],
  [SIZES.X_SMALL]: PX_TO_REM[12],
  [SIZES.SMALL]: PX_TO_REM[16]
};

const eoraStandardRadii = EORA_NUMBERS.CORE.RADIUS;

export const standardBorderRadiusSizes: Record<StandardBorderRadiusSize, PxToRem> = {
  [OFFGRID_SIZES.OFFGRID_2]: PX_TO_REM[eoraStandardRadii.SIZE_BORDER_RADIUS_OFFGRID_2],
  [SIZES.SMALL]: PX_TO_REM[eoraStandardRadii.SIZE_BORDER_RADIUS_SMALL],
  [OFFGRID_SIZES.OFFGRID_7]: PX_TO_REM[eoraStandardRadii.SIZE_BORDER_RADIUS_OFFGRID_7],
  [SIZES.MEDIUM]: PX_TO_REM[eoraStandardRadii.SIZE_BORDER_RADIUS_MEDIUM],
  [OFFGRID_SIZES.OFFGRID_9]: PX_TO_REM[eoraStandardRadii.SIZE_BORDER_RADIUS_OFFGRID_9],
  [OFFGRID_SIZES.OFFGRID_10]: PX_TO_REM[eoraStandardRadii.SIZE_BORDER_RADIUS_OFFGRID_10],
  [OFFGRID_SIZES.OFFGRID_12]: PX_TO_REM[eoraStandardRadii.SIZE_BORDER_RADIUS_OFFGRID_12],
  [OFFGRID_SIZES.OFFGRID_14]: PX_TO_REM[eoraStandardRadii.SIZE_BORDER_RADIUS_OFFGRID_14],
  [SIZES.LARGE]: PX_TO_REM[eoraStandardRadii.SIZE_BORDER_RADIUS_LARGE],
  [OFFGRID_SIZES.OFFGRID_18]: PX_TO_REM[eoraStandardRadii.SIZE_BORDER_RADIUS_OFFGRID_18],
  [OFFGRID_SIZES.OFFGRID_20]: PX_TO_REM[eoraStandardRadii.SIZE_BORDER_RADIUS_OFFGRID_20],
  [OFFGRID_SIZES.OFFGRID_22]: PX_TO_REM[eoraStandardRadii.SIZE_BORDER_RADIUS_OFFGRID_22],
  [SIZES.X_LARGE]: PX_TO_REM[eoraStandardRadii.SIZE_BORDER_RADIUS_XLARGE],
  [OFFGRID_SIZES.OFFGRID_26]: PX_TO_REM[eoraStandardRadii.SIZE_BORDER_RADIUS_OFFGRID_26]
};

export const themeBrandRadiusSizes = (theme: Theme): Record<ButtonTabBadgeRadiusSize | SpeicalBorderRadiusSize, PxToRem> => {
  const themeName: string = eoraThemeName[theme] ?? eoraThemeName.cmc;

  const getBrandRadii = () => {
    try {
      return EORA_NUMBERS.BRAND[themeName as keyof typeof EORA_NUMBERS.BRAND].BRAND_RADIUS;
    } catch {
      return EORA_NUMBERS.BRAND.CMC.BRAND_RADIUS;
    }
  };

  const brandRadii = getBrandRadii();
  const buttonTabRadii = brandRadii.BUTTON_TAB_BADGE_RADIUS;

  return {
    [SIZES.XX_SMALL]: PX_TO_REM[buttonTabRadii.WL_SIZE_BUTTONBADGETAB_BORDER_RADIUS_CUSTOM_8],
    [OFFGRID_SIZES.OFFGRID_9]: PX_TO_REM[buttonTabRadii.WL_SIZE_BUTTONBADGETAB_BORDER_RADIUS_CUSTOM_9],
    [OFFGRID_SIZES.OFFGRID_10]: PX_TO_REM[buttonTabRadii.WL_SIZE_BUTTONBADGETAB_BORDER_RADIUS_CUSTOM_10],
    [SIZES.X_SMALL]: PX_TO_REM[buttonTabRadii.WL_SIZE_BUTTONBADGETAB_BORDER_RADIUS_XSMALL],
    [SIZES.SMALL]: PX_TO_REM[buttonTabRadii.WL_SIZE_BUTTONBADGETAB_BORDER_RADIUS_SMALL],
    [SIZES.MEDIUM]: PX_TO_REM[buttonTabRadii.WL_SIZE_BUTTONBADGETAB_BORDER_RADIUS_MEDIUM],
    [SIZES.LARGE]: PX_TO_REM[buttonTabRadii.WL_SIZE_BUTTONBADGETAB_BORDER_RADIUS_LARGE],
    [SIZES.NONE]: PX_TO_REM[buttonTabRadii.WL_SIZE_BUTTONBADGETAB_BORDER_RADIUS_NONE],
    input: PX_TO_REM[brandRadii.WL_SIZE_INPUT_BORDER_RADIUS],
    panel: PX_TO_REM[brandRadii.WL_SIZE_PANEL_BORDER_RADIUS]
  };
};

export interface IBorderRadiusProps {
  borderRadius?: boolean | StandardBorderRadiusSize;
}

const legacyBorderRadiusSizeMap: Record<BorderRadiusSize, StandardBorderRadiusSize> = {
  [SIZES.MICRO]: SIZES.SMALL,
  [SIZES.XXX_SMALL]: SIZES.MEDIUM,
  [SIZES.XX_SMALL]: OFFGRID_SIZES.OFFGRID_10,
  [SIZES.X_SMALL]: OFFGRID_SIZES.OFFGRID_12,
  [SIZES.SMALL]: SIZES.SMALL
};
const convertFromLegacyBorderRadiusSize = (radius: true | StandardBorderRadiusSize): StandardBorderRadiusSize => {
  if (radius === undefined || radius === true) return SIZES.MEDIUM;
  if (Object.keys(legacyBorderRadiusSizeMap).includes(radius)) return legacyBorderRadiusSizeMap[radius as BorderRadiusSize];
  return radius as StandardBorderRadiusSize;
};

/**
 * ### DEV NOTE: Mismatch and overlap between EORA V1 and V2 sizes
 *
 * EORA V1 sizes MICRO | XXX_SMALL | XX_SMALL (default 10px) | X_SMALL | SMALL
 *
 * EORA V2 sizes SMALL | MEDIUM (default 8px) | LARGE | X_LARGE | <OFFGIRD SIZES>
 *
 * For backward compatibility, if V1 size is provided function will map to V2 size as follows:
 *
 * ````
 *  MICRO > SMALL
 *  XXX_SMALL > MEDIUM
 *  XX_SMALL > OFFGRID_10
 *  X_SMALL > OFFGRID_12
 *  SMALL > SMALL - breaking change
 * ````
 */
export const useBorderRadiusCss = (defaultRadius?: boolean | StandardBorderRadiusSize) => css<IBorderRadiusProps>`
  ${({ theme, borderRadius = defaultRadius }) =>
    borderRadius &&
    css`
      border-radius: ${theme.standardBorderRadius[convertFromLegacyBorderRadiusSize(borderRadius)]};
    `}
`;

/**
 * @deprecated For panels use usePanelBorderRadiusCss
 */
export const useTopBorderRadiusCss = (defaultRadius?: boolean | StandardBorderRadiusSize) => css<IBorderRadiusProps>`
  ${({ theme, borderRadius = defaultRadius }) =>
    borderRadius &&
    css`
      border-top-left-radius: ${theme.standardBorderRadius[convertFromLegacyBorderRadiusSize(borderRadius)]};
      border-top-right-radius: ${theme.standardBorderRadius[convertFromLegacyBorderRadiusSize(borderRadius)]};
    `}
`;

export const usePanelBorderRadiusCss = (topOnly?: boolean) => css`
  ${({ theme }) => css`
    border-top-left-radius: ${theme.brandBorderRadius.panel};
    border-top-right-radius: ${theme.brandBorderRadius.panel};
    border-bottom-right-radius: ${topOnly ? 0 : theme.brandBorderRadius.panel};
    border-bottom-left-radius: ${topOnly ? 0 : theme.brandBorderRadius.panel};
  `}
`;

export const generateBorderRadii = () => ({
  /**
   * @deprecated Use standardBorderRadius
   */
  borderRadius: borderRadiusSizes,
  /**
   * @deprecated Use buttonTabBadgeBorderRadius
   */
  buttonBorderRadius,
  standardBorderRadius: standardBorderRadiusSizes,
  brandBorderRadius: themeBrandRadiusSizes('cmc')
});
